"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { useMobileMenu } from "@/contexts/frontend/mobile-menu-context";
import { cn } from "@/lib/utils";

interface MobNavSubmenuTriggerProps {
	children: ReactNode;
	id: string;
	ariaLabel: string;
}

export function MobNavSubmenuTrigger({ children, id, ariaLabel }: MobNavSubmenuTriggerProps) {
	const navigation = useMobileMenu();

	return (
		<button
			id={`${id}-trigger`}
			type="button"
			className="p-1 shrink-0 rounded transition-[color] hover:text-(--link-hover-color) flex-1 flex justify-end"
			onClick={() => navigation?.setExpandedIds((ids) => [...ids, id])}
			aria-controls={`${id}-submenu`}
			aria-expanded={navigation?.expandedIds.includes(id) ?? false}
			aria-label={ariaLabel}
		>
			{children}
		</button>
	);
}

interface MobNavSubmenuClientProps {
	children: ReactNode;
	backLabel: ReactNode;
	id: string;
	label: string;
	className?: string;
}

export function MobNavSubmenuClient({ children, backLabel, id, label, className }: MobNavSubmenuClientProps) {
	const navigation = useMobileMenu();
	const isActive = navigation?.expandedIds.at(-1) === id;
	const backButton = useRef<HTMLButtonElement>(null);
	const restoreFocus = useRef(false);

	useEffect(() => {
		if (isActive) {
			backButton.current?.focus({ preventScroll: true });
		} else if (restoreFocus.current) {
			document.getElementById(`${id}-trigger`)?.focus();
			restoreFocus.current = false;
		}
	}, [id, isActive]);

	return (
		<nav
			id={`${id}-submenu`}
			aria-label={label}
			aria-hidden={!isActive}
			inert={!isActive}
			className={cn(
				"absolute inset-0 z-10 flex flex-col gap-4 overflow-y-auto bg-inherit p-4 transition-transform duration-300 ease-in-out motion-reduce:transition-none",
				className,
				navigation?.expandedIds.includes(id) ? "translate-x-0" : "-translate-x-full pointer-events-none",
			)}
			onKeyDown={(event) => {
				if (event.key !== "Escape") return;
				event.preventDefault();
				event.stopPropagation();
				restoreFocus.current = true;
				navigation?.setExpandedIds((ids) => ids.slice(0, -1));
			}}
		>
			<button
				ref={backButton}
				type="button"
				className="flex items-center gap-2 self-start"
				onClick={() => {
					restoreFocus.current = true;
					navigation?.setExpandedIds((ids) => ids.slice(0, -1));
				}}
			>
				{backLabel}
			</button>
			{children}
		</nav>
	);
}
